import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, SlidersHorizontal, X } from "lucide-react";
import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import AuthorBio from "../components/AuthorBio";
import ProductCard from "../components/ProductCard";
import QuickAnswer from "../components/QuickAnswer";
import { getBrandDetail, getSpecials, getStrainLibrary } from "../lib/api";
import { classifySpecial } from "../lib/promos";
import MenuFilters from "./menu/MenuFilters";
import useFacetedFilters from "./menu/useFacetedFilters";

// Read+write helpers for URL-backed array params (e.g. ?type=INDICA,SATIVA).
const readArrayParam = (sp, key) => {
    const raw = sp.get(key);
    return raw ? raw.split(",").filter(Boolean) : [];
};

export default function BrandDetail() {
    const { storeSlug, brandSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [specials, setSpecials] = useState([]);
    const [strainLib, setStrainLib] = useState([]);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [err, setErr] = useState(null);

    // Filter state lives in the URL so /menu/.../brand/jetpacks?category=PRE_ROLLS&type=INDICA
    // is shareable + survives back/forward navigation.
    const categoryFilter = searchParams.get("category") || "ALL";
    const strainFilter = searchParams.get("strain") || "ALL";
    const selectedStrains = readArrayParam(searchParams, "type");
    const selectedWeights = readArrayParam(searchParams, "weight");
    const selectedPotency = readArrayParam(searchParams, "potency");

    const setParam = (key, val, { replace = false } = {}) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (val === null || val === undefined || val === "" || val === "ALL")
                    next.delete(key);
                else next.set(key, val);
                return next;
            },
            { replace }
        );
    };
    const setCategoryFilter = (v) => setParam("category", v === "ALL" ? null : v);
    const setStrainFilter = (v) => setParam("strain", v === "ALL" ? null : v);
    const toggleArr = (key, prevVals) => (val) => {
        const next = prevVals.includes(val)
            ? prevVals.filter((v) => v !== val)
            : [...prevVals, val];
        setParam(key, next.length ? next.join(",") : null);
    };
    const toggleStrainType = toggleArr("type", selectedStrains);
    const toggleWeight = toggleArr("weight", selectedWeights);
    const togglePotency = toggleArr("potency", selectedPotency);

    useEffect(() => {
        let cancelled = false;
        Promise.resolve().then(() => {
            if (!cancelled) {
                setData(null);
                setErr(null);
            }
        });
        getBrandDetail(storeSlug, brandSlug)
            .then((d) => { if (!cancelled) Promise.resolve().then(() => setData(d)); })
            .catch((e) => {
                if (cancelled) return;
                const v = e?.response?.status === 404 ? "not found" : "error";
                Promise.resolve().then(() => setErr(v));
            });
        getSpecials(storeSlug)
            .then((d) => { if (!cancelled) Promise.resolve().then(() => setSpecials(d?.specials || [])); })
            .catch(() => {});
        // Strain library — used to resolve which strain each of this brand's
        // products belongs to (via product-name → whitelist matching).
        getStrainLibrary(storeSlug)
            .then((d) => { if (!cancelled) Promise.resolve().then(() => setStrainLib(d?.strains || [])); })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug, brandSlug]);

    const specialsById = useMemo(() => {
        const idx = {};
        specials.forEach((s) => {
            const c = classifySpecial(s);
            if (c) idx[c.id] = c;
        });
        return idx;
    }, [specials]);

    // Map each strain in the library to its products WITHIN this brand. Uses
    // the same longest-match heuristic the backend uses: look for the strain
    // canonical name as a whole-word substring inside the product name.
    const products = data?.products || [];
    const { strainsForBrand, productsByStrain } = useMemo(() => {
        if (!strainLib.length || !products.length) {
            return { strainsForBrand: [], productsByStrain: {} };
        }
        // Sort matchers longest-first so "Ice Cream Cake" wins over "Ice".
        const matchers = [...strainLib].sort(
            (a, b) => b.name.length - a.name.length
        );
        const byStrain = {};
        const norm = (s) =>
            " " + (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim() + " ";
        for (const p of products) {
            const hay = norm(p.name);
            for (const s of matchers) {
                if (hay.includes(` ${s.name.toLowerCase()} `)) {
                    (byStrain[s.slug] = byStrain[s.slug] || []).push(p);
                    break;
                }
            }
        }
        // Strain chip counts reflect the active CATEGORY filter so combined
        // filters stay coherent (e.g. "Indica" count drops when "Vapes" is
        // selected).
        const inCat = (p) => categoryFilter === "ALL" || p.category === categoryFilter;
        const list = strainLib
            .map((s) => ({
                ...s,
                brand_count: (byStrain[s.slug] || []).filter(inCat).length,
            }))
            .filter((s) => s.brand_count > 0);
        return { strainsForBrand: list, productsByStrain: byStrain };
    }, [strainLib, products, categoryFilter]);

    // Auto-reset strain filter if a category change made it disappear from
    // the visible chip row (e.g. switched to "Vapes" and the picked strain
    // has no vape SKUs).
    useEffect(() => {
        if (strainFilter === "ALL") return;
        if (!strainsForBrand.some((s) => s.slug === strainFilter)) {
            // Use replace:true here so the auto-reset doesn't push a new
            // history entry — otherwise browser BACK would land on the
            // invalid filter and we'd reset again in a loop.
            Promise.resolve().then(() =>
                setParam("strain", null, { replace: true })
            );
        }
    }, [strainsForBrand, strainFilter]);

    // The pool fed to the facet pipeline is the brand's products restricted
    // to the active CATEGORY chip (so sidebar facet counts narrow to the
    // category you're browsing). The strain chip filter and sidebar filters
    // are then applied on top.
    const poolForFacets = useMemo(() => {
        if (categoryFilter === "ALL") return products;
        return products.filter((p) => p.category === categoryFilter);
    }, [products, categoryFilter]);

    const {
        applyFilters: applyFacetFilters,
        strainOptions,
        weightOptions,
        potencyOptions,
    } = useFacetedFilters(poolForFacets, {
        selectedStrains,
        selectedWeights,
        selectedPotency,
    });

    const filteredProducts = useMemo(() => {
        let list = applyFacetFilters(poolForFacets);
        if (strainFilter !== "ALL") {
            const allowed = new Set(productsByStrain[strainFilter] || []);
            list = list.filter((p) => allowed.has(p));
        }
        return list;
    }, [applyFacetFilters, poolForFacets, strainFilter, productsByStrain]);

    // Category options derived from the brand's actual products — preserves
    // original casing, sorted by descending count then alpha.
    const categoryOptions = useMemo(() => {
        const counts = {};
        for (const p of products) {
            const c = p.category;
            if (!c) continue;
            counts[c] = (counts[c] || 0) + 1;
        }
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name, count]) => ({ name, count }));
    }, [products]);

    const sidebarActiveCount =
        selectedStrains.length + selectedWeights.length + selectedPotency.length;
    const totalActiveCount =
        sidebarActiveCount +
        (categoryFilter !== "ALL" ? 1 : 0) +
        (strainFilter !== "ALL" ? 1 : 0);
    const clearAllBrandFilters = () => {
        setSearchParams(new URLSearchParams(), { replace: true });
    };

    if (err === "not found") {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    Brand not available here.
                </h1>
                <Link
                    to={`/menu/${storeSlug}`}
                    className="text-zen-olive underline"
                >
                    Back to menu
                </Link>
            </div>
        );
    }
    if (err || !data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                {err ? "Live menu temporarily unavailable. Please try again." : "Loading…"}
            </div>
        );
    }

    const { brand, product_count: productCount } = data;
    const canonical = `https://weeddispensarynearme.com/menu/${storeSlug}/brand/${brandSlug}`;

    const schema = {
        "@context": "https://schema.org",
        "@type": "Brand",
        name: brand.name,
        ...(brand.imageUrl ? { logo: brand.imageUrl } : {}),
        ...(brand.description ? { description: brand.description } : {}),
        url: canonical,
    };

    // Attach an Article schema when the Phase-H editorial exists — YMYL byline
    // signal for cannabis long-form content. Google/Perplexity read this as an
    // expert-authored review by the ZenZest Editorial Team.
    const schemas = [schema];
    if (data.editorial?.sections) {
        schemas.push({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${brand.name} at ZenZest — brand review`.slice(0, 110),
            description: data.editorial.blurb?.slice(0, 155) || brand.description?.slice(0, 155),
            image: brand.imageUrl || "https://weeddispensarynearme.com/zenzest-badge.png",
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            url: canonical,
            datePublished: data.editorial.updated_at,
            dateModified: data.editorial.updated_at,
            author: {
                "@type": "Organization",
                name: "ZenZest Editorial Team",
                url: "https://weeddispensarynearme.com/",
            },
            publisher: {
                "@type": "Organization",
                name: "ZenZest",
                logo: {
                    "@type": "ImageObject",
                    url: "https://weeddispensarynearme.com/zenzest-badge.png",
                },
            },
        });
    }

    return (
        <>
            <SEOHead
                title={`${brand.name} — Cannabis at ZenZest | ${productCount} live products`}
                description={
                    brand.description?.slice(0, 155) ||
                    `Shop ${productCount} live ${brand.name} cannabis products at ZenZest. NY OCM-licensed. Real-time inventory and pricing.`
                }
                canonical={canonical}
                image={brand.imageUrl || undefined}
            />
            <SchemaJsonLd schema={schemas} />

            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10 sm:py-14">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <Link
                            to={`/menu/${storeSlug}`}
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] opacity-80 hover:opacity-100"
                        >
                            <ArrowLeft size={12} /> Back to menu
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <p className="text-[11px] uppercase tracking-[0.3em] opacity-80 mb-3">
                                Brand · {productCount} products live
                            </p>
                            <h1
                                data-testid="brand-hero-name"
                                className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight tracking-tight"
                            >
                                {brand.name}
                            </h1>
                            {brand.description && (
                                <p className="mt-5 text-sm sm:text-base opacity-85 max-w-2xl leading-relaxed">
                                    {brand.description}
                                </p>
                            )}
                        </div>
                        {brand.imageUrl && (
                            <div className="lg:col-span-4">
                                <div className="aspect-square bg-zen-cream/5 border border-zen-cream/10 p-8 sm:p-12 flex items-center justify-center">
                                    <img
                                        src={brand.imageUrl}
                                        alt={`${brand.name} logo`}
                                        data-testid="brand-hero-logo"
                                        className="max-w-full max-h-full object-contain"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Phase H — Brand Authority editorial. Lazy-loaded with the
                brand detail response; gracefully hidden if not yet
                generated. Adds ~1500 words of unique SEO content. */}
            {data.editorial?.sections && (
                <>
                    {data.editorial.blurb && (
                        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 pt-6">
                            <QuickAnswer
                                question={`What is the ${brand.name} cannabis brand?`}
                                answer={data.editorial.blurb}
                                testidPrefix="brand-quick-answer"
                            />
                        </div>
                    )}
                    <BrandEditorial editorial={data.editorial} brandName={brand.name} storeSlug={storeSlug} />
                    <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
                        <AuthorBio reviewedDate={data.editorial.updated_at} />
                    </div>
                </>
            )}

            {/* Sticky filter toolbar — Category chips + Strain chips both
                pinned together when scrolling so picking a filter on a long
                brand catalog doesn't require scrolling back up. Mirrors the
                live menu page's combined toolbar. The mobile Filters toggle
                opens a drawer with the same sidebar axes used on the menu
                page (Strain Type, Weight, THC Potency). */}
            {(categoryOptions.length > 1 || strainsForBrand.length > 0) && (
                <section className="sticky top-[72px] z-30 bg-zen-bg/90 backdrop-blur-xl border-b border-zen-ink/10">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-3 space-y-2">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setMobileFiltersOpen(true)}
                                data-testid="brand-mobile-filters-toggle"
                                className="lg:hidden shrink-0 inline-flex items-center gap-2 px-3 py-2 border border-zen-ink/15 text-zen-ink text-[11px] uppercase tracking-[0.22em] hover:border-zen-ink/40"
                            >
                                <SlidersHorizontal size={14} />
                                Filters
                                {sidebarActiveCount > 0 && (
                                    <span className="bg-zen-olive text-zen-bg text-[10px] w-5 h-5 inline-flex items-center justify-center rounded-full">
                                        {sidebarActiveCount}
                                    </span>
                                )}
                            </button>
                            {categoryOptions.length > 1 && (
                                <>
                                    <span className="shrink-0 text-[10px] uppercase tracking-[0.28em] text-zen-olive pr-1 hidden sm:inline">
                                        Category
                                    </span>
                                    <div
                                        data-testid="brand-category-chips"
                                        className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0"
                                    >
                                        <button
                                            onClick={() => setCategoryFilter("ALL")}
                                            data-testid="brand-category-all"
                                            className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                                categoryFilter === "ALL"
                                                    ? "bg-zen-ink text-zen-cream border-zen-ink"
                                                    : "border-zen-line text-zen-ink-soft hover:border-zen-ink/40"
                                            }`}
                                        >
                                            All ({products.length})
                                        </button>
                                        {categoryOptions.map((c) => (
                                            <button
                                                key={c.name}
                                                onClick={() => setCategoryFilter(c.name)}
                                                data-testid={`brand-category-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                                    categoryFilter === c.name
                                                        ? "bg-zen-ink text-zen-cream border-zen-ink"
                                                        : "border-zen-line text-zen-ink-soft hover:border-zen-ink/40"
                                                }`}
                                            >
                                                {c.name.replace(/_/g, " ")} ({c.count})
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        {strainsForBrand.length > 0 && (
                            <div className="flex items-center gap-3">
                                <span className="shrink-0 text-[10px] uppercase tracking-[0.28em] text-zen-olive pr-1 hidden sm:inline">
                                    Strain
                                </span>
                                <div
                                    data-testid="brand-strain-chips"
                                    className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0"
                                >
                                    <button
                                        onClick={() => setStrainFilter("ALL")}
                                        data-testid="brand-strain-all"
                                        className={`shrink-0 px-3 py-1 text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                                            strainFilter === "ALL"
                                                ? "bg-zen-olive border-zen-olive text-zen-cream"
                                                : "border-zen-line text-zen-ink-soft hover:border-zen-olive"
                                        }`}
                                    >
                                        All
                                    </button>
                                    {strainsForBrand.map((s) => (
                                        <button
                                            key={s.slug}
                                            onClick={() => setStrainFilter(s.slug)}
                                            data-testid={`brand-strain-${s.slug}`}
                                            className={`shrink-0 px-3 py-1 text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                                                strainFilter === s.slug
                                                    ? "bg-zen-olive border-zen-olive text-zen-cream"
                                                    : "border-zen-line text-zen-ink-soft hover:border-zen-olive"
                                            }`}
                                        >
                                            {s.name} ({s.brand_count})
                                        </button>
                                    ))}
                                </div>
                                {totalActiveCount > 0 && (
                                    <button
                                        onClick={clearAllBrandFilters}
                                        data-testid="brand-clear-filters"
                                        className="shrink-0 text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink inline-flex items-center gap-1"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Body — sidebar filters + product grid (desktop) / drawer (mobile) */}
            <section className="bg-zen-bg max-w-[1400px] mx-auto px-3 sm:px-5 lg:px-10 py-10">
                <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
                    <aside className="hidden lg:block">
                        <MenuFilters
                            selectedStrains={selectedStrains}
                            selectedWeights={selectedWeights}
                            selectedPotency={selectedPotency}
                            strainOptions={strainOptions}
                            weightOptions={weightOptions}
                            potencyOptions={potencyOptions}
                            brandOptions={[]}
                            onToggleStrain={toggleStrainType}
                            onToggleWeight={toggleWeight}
                            onTogglePotency={togglePotency}
                        />
                    </aside>

                    {mobileFiltersOpen && (
                        <div
                            data-testid="brand-mobile-filters-drawer"
                            className="fixed inset-0 z-[130] bg-zen-ink/40 backdrop-blur-sm lg:hidden"
                            onClick={() => setMobileFiltersOpen(false)}
                        >
                            <aside
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-0 left-0 bottom-0 w-[85%] max-w-sm bg-zen-bg p-6 overflow-y-auto"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive">
                                        Filters
                                    </p>
                                    <button
                                        onClick={() => setMobileFiltersOpen(false)}
                                        aria-label="Close filters"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                                <MenuFilters
                                    selectedStrains={selectedStrains}
                                    selectedWeights={selectedWeights}
                                    selectedPotency={selectedPotency}
                                    strainOptions={strainOptions}
                                    weightOptions={weightOptions}
                                    potencyOptions={potencyOptions}
                                    brandOptions={[]}
                                    onToggleStrain={toggleStrainType}
                                    onToggleWeight={toggleWeight}
                                    onTogglePotency={togglePotency}
                                />
                                {sidebarActiveCount > 0 && (
                                    <button
                                        onClick={() => {
                                            setSearchParams(
                                                (prev) => {
                                                    const next = new URLSearchParams(prev);
                                                    next.delete("type");
                                                    next.delete("weight");
                                                    next.delete("potency");
                                                    return next;
                                                },
                                                { replace: true }
                                            );
                                            setMobileFiltersOpen(false);
                                        }}
                                        data-testid="brand-mobile-clear-filters"
                                        className="mt-6 w-full py-2.5 border border-zen-ink/20 text-[11px] uppercase tracking-[0.22em] text-zen-ink hover:bg-zen-surface"
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </aside>
                        </div>
                    )}

                    <div>
                {filteredProducts.length === 0 ? (
                    <div className="text-center py-12 border border-zen-ink/10 bg-zen-surface">
                        <p className="font-serif text-2xl text-zen-ink font-light mb-3">
                            {totalActiveCount === 0
                                ? "No products in stock right now."
                                : "No products match these filters right now."}
                        </p>
                        {totalActiveCount > 0 ? (
                            <button
                                onClick={clearAllBrandFilters}
                                data-testid="brand-filters-reset"
                                className="text-[11px] uppercase tracking-[0.25em] text-zen-olive border-b border-zen-olive/40 pb-1"
                            >
                                Reset filters
                            </button>
                        ) : (
                            <Link
                                to={`/menu/${storeSlug}`}
                                className="text-[11px] uppercase tracking-[0.25em] text-zen-olive border-b border-zen-olive/40 pb-1"
                            >
                                Browse full menu
                            </Link>
                        )}
                    </div>
                ) : (
                    <div
                        data-testid="brand-product-grid"
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3"
                    >
                        {filteredProducts.map((p, i) => (
                            <ProductCard
                                key={`${p.id}-${i}`}
                                product={p}
                                storeSlug={storeSlug}
                                specialsById={specialsById}
                            />
                        ))}
                    </div>
                )}
                    </div>
                </div>
            </section>

            {/* Brand FAQ + similar brands — rendered AFTER the product
                grid so shoppers see live inventory first; Google still
                indexes the deep editorial content above and the FAQ
                schema below. */}
            {data.editorial?.sections && (
                <BrandFaqAndSiblings
                    editorial={data.editorial}
                    brandName={brand.name}
                    storeSlug={storeSlug}
                />
            )}
        </>
    );
}


/* ---------------------------------------------------------------------- *
 *  Phase H — Brand Authority editorial blocks (in-file to keep BrandDetail
 *  self-contained; ~50 lines each, no shared business logic).
 * ---------------------------------------------------------------------- */

function BrandEditorial({ editorial, brandName, storeSlug }) {
    const s = editorial.sections || {};
    const blocks = [
        ["story_deep", "The story"],
        ["aesthetic_deep", "Aesthetic & shopper persona"],
        ["cultivation_or_extraction", "Cultivation & craft"],
        ["hero_products", "What to try first"],
        ["occasion_deep", "When this brand shines"],
        ["provenance", "Provenance"],
    ].filter(([k]) => s[k]);
    if (!blocks.length && !editorial.blurb) return null;
    return (
        <section
            data-testid="brand-editorial"
            className="bg-zen-bg border-b border-zen-ink/10"
        >
            <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12 lg:py-16">
                {editorial.blurb && (
                    <p
                        data-testid="brand-editorial-blurb"
                        className="font-serif text-xl sm:text-2xl text-zen-ink leading-[1.55] mb-10 max-w-3xl"
                    >
                        {editorial.blurb}
                    </p>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7">
                    {blocks.map(([k, label]) => (
                        <div
                            key={k}
                            data-testid={`brand-editorial-${k}`}
                            className="bg-white border border-zen-line p-5 sm:p-6"
                        >
                            <h2 className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                                {label}
                            </h2>
                            <p className="text-[15px] sm:text-base leading-[1.75] text-zen-ink/90 whitespace-pre-line">
                                {s[k]}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}


function BrandFaqAndSiblings({ editorial, brandName, storeSlug }) {
    const sections = editorial.sections || {};
    const faq = Array.isArray(sections.faq) ? sections.faq : [];
    const similar = Array.isArray(sections.similar_brands) ? sections.similar_brands : [];
    if (!faq.length && !similar.length) return null;

    // JSON-LD FAQPage schema — gives Google rich-snippet eligibility for
    // brand-page Q&A right alongside the product grid.
    const faqSchema = faq.length
        ? {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faq.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
          }
        : null;

    return (
        <section
            data-testid="brand-faq-siblings"
            className="bg-zen-surface border-t border-zen-line"
        >
            <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12 lg:py-16 space-y-12">
                {faq.length > 0 && (
                    <div>
                        <h2 className="font-serif text-2xl sm:text-3xl font-light text-zen-ink mb-6">
                            {brandName} — frequently asked
                        </h2>
                        <div
                            data-testid="brand-faq-list"
                            className="border border-zen-line bg-white divide-y divide-zen-line"
                        >
                            {faq.map((f, i) => (
                                <details
                                    key={i}
                                    data-testid={`brand-faq-${i}`}
                                    className="group px-5 py-4"
                                >
                                    <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-serif text-base text-zen-ink leading-snug">
                                        <span>{f.q}</span>
                                        <span className="text-zen-olive text-xl leading-none group-open:rotate-45 transition-transform">
                                            +
                                        </span>
                                    </summary>
                                    <p className="text-[14px] text-zen-ink/85 leading-[1.7] mt-3">
                                        {f.a}
                                    </p>
                                </details>
                            ))}
                        </div>
                        {faqSchema && (
                            <script
                                type="application/ld+json"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: JSON.stringify(faqSchema)
                                        .replace(/</g, "\\u003c")
                                        .replace(/>/g, "\\u003e")
                                        .replace(/&/g, "\\u0026"),
                                }}
                            />
                        )}
                    </div>
                )}

                {similar.length > 0 && (
                    <div>
                        <h2 className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-5">
                            If you like {brandName}, also explore
                        </h2>
                        <div
                            data-testid="brand-similar-list"
                            className="grid grid-cols-1 md:grid-cols-2 gap-3"
                        >
                            {similar.map((b, i) => {
                                const slug = (b.name || "")
                                    .toLowerCase()
                                    .normalize("NFKD")
                                    .replace(/[^a-z0-9]+/g, "-")
                                    .replace(/^-+|-+$/g, "");
                                const cls =
                                    "bg-white border border-zen-line p-4 hover:border-zen-olive transition";
                                const inner = (
                                    <>
                                        <p className="font-serif text-lg text-zen-ink leading-snug">
                                            {b.name}
                                        </p>
                                        {b.why && (
                                            <p className="text-[13px] text-zen-ink-soft mt-1 leading-relaxed">
                                                {b.why}
                                            </p>
                                        )}
                                    </>
                                );
                                return (
                                    <Link
                                        key={i}
                                        to={`/menu/${storeSlug}/brand/${slug}`}
                                        data-testid={`brand-similar-${i}`}
                                        className={`${cls} block group`}
                                    >
                                        {inner}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
